/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import session from '../../../../../share/session';
import config from '../../../../../share/config';
import util from '../../../../../share/util/utils';
import error from '../../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTE: 'execute',
  EXECUTE_SELECT_TRANSMITTER: 'executeSelectTransmitter',
  EXECUTE_OUTPUT_CHANGE_POINT: 'executeOutputChangePoint',
  EXECUTE_SELECT_VALUE: 'executeSelectValue',
  SUCCESS: 'success',
  FAILED_EXECUTE: 'failedExecute',
  FAILED: 'failed'
};

export default {
  name: 'requirementSpSMT1',
  data() {
    return {
      phase: 'start',
      isLoading: false,
      isDisabled: false,
      isDisabledSelect: true,           // 発信機選択表示画面のOKボタン活性状態
      transmitterList: [],              // 発信機選択リスト
      selectTransmitterDid: '',         // 選択した発信機DID
      displayInfoSelectTransmitter: '', // 選択した発信機名メッセージ
      valueList: [],                    // 発信機設定値選択リスト
      displayInfoCurrentValue: '',      // 選択した発信機の現在値メッセージ
      selectValue: '',                  // 選択した設定値
      modal_special_requirement_SMT1: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.EXECUTE:
        case PHASE.EXECUTE_SELECT_TRANSMITTER:
        case PHASE.EXECUTE_OUTPUT_CHANGE_POINT:
        case PHASE.EXECUTE_SELECT_VALUE:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    // 初期化メソッドを呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化メソッド */
    async init() {
      this.modal_special_requirement_SMT1 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // リソース(特殊機能_DID)のi18nキー取得
      this.i18nSpwsDidKey = `spwsDid${this.systemId}`;
      // 初期データ読み出し処理
      this.standbyPostGet();
    },

    //----- htmlイベントメソッド -----------------------------------------------//

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      // 終了メソッドを呼び出す
      await this.exitPostGet();
      this.$emit('close-wizard');
      this.modal_special_requirement_SMT1 = false;
    },

    /** 実行画面OKボタンクリックイベント */
    async clickExecuteSelectTransmitter() {
      // 発信機選択表示画面を表示
      this.showExecuteSelectTransmitter();
    },

    /** 発信機選択表示画面OKボタンクリックイベント */
    async clickReadPostGet() {
      // データ読み出しメソッドを呼び出す
      await this.readPostGet();
    },

    /** 発信機選択処理 */
    clickTransmitter: function () {
      // 選択されていればOKボタンを活性
      if (this.selectTransmitterDid !== '' && this.isDisabledSelect) {
        this.isDisabledSelect = false;
        // 選択項目が見切れる場合、項目の全文字列を選択状態とする
        const formSelect = document.getElementById('transmitterFormSelect');
        if (formSelect.scrollWidth > formSelect.clientWidth) {
          // 各optionタグのwidthをスクロール幅まで拡張（間延びを考慮し補正も掛ける）
          const optionWidth = formSelect.scrollWidth - 10;
          for (const option of Array.from(formSelect.children)) {
            option.style.width = optionWidth + 'px';
          }
        }
      }
    },

    /** 出力変更箇所表示画面OKボタンクリックイベント */
    async clickExecuteSelectValue() {
      // 設定値変更選択表示画面を表示
      this.showExecuteSelectValue();
    },

    /** 設定値変更選択表示画面OKボタンクリックイベント */
    async clickWritePostGet() {
      // データ書き込みメソッドを呼び出す
      await this.writePostGet();
    },

    /** 実行画面遷移ボタンクリックイベント */
    async clickExecute() {
      // 実行画面を表示
      this.showExecute();
      // 内部保持データを初期化
      this.isDisabledSelect = true;
      this.selectTransmitterDid = '';
      this.valueList = [];
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** 実行表示メソッド */
    showExecute() {
      this.phase = PHASE.EXECUTE;
    },

    /** 発信機選択表示メソッド */
    showExecuteSelectTransmitter() {
      this.phase = PHASE.EXECUTE_SELECT_TRANSMITTER;
    },

    /** 出力変更箇所表示メソッド */
    showExecuteOutputChangePoint() {
      this.phase = PHASE.EXECUTE_OUTPUT_CHANGE_POINT;
    },

    /** 設定値変更選択表示メソッド */
    showExecuteSelectValue() {
      this.phase = PHASE.EXECUTE_SELECT_VALUE;
    },

    /** 成功表示メソッド */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** 失敗表示メソッド */
    showFailedExecute() {
      this.phase = PHASE.FAILED_EXECUTE;
    },

    /** 通信エラー表示メソッド */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /**
     * 初期データ読み出しメソッド
     */
    async standbyPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'standby'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 発信機選択リストを生成
            this.transmitterList = response.data.user_data.dids.map(item => {
              return {
                // 発信機名は翻訳結果の文言を設定
                text: this.$t(`${this.i18nSpwsDidKey}.${item}_1_all`),
                value: item,
              };
            });
            // 実行画面に遷移
            this.showExecute();
          } else {
            // [成功以外]
            switch (response.data.async_code) {
              case error.WSP_EXECUTION_ANTENNA_OUTPUT_VALUE_SETTING_FAILURE_ERROR:
                // [失敗]
                // 失敗画面に遷移
                this.showFailedExecute();
                break;
              default:
                // [上記以外のエラー]
                // 通信エラー画面に遷移
                this.showUnexpectedError();
                break;
            }
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * データ読み出しメソッド
     */
    async readPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;
        this.isDisabledSelect = true; // 発信機選択表示画面のOKボタンは個別制御

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'read',
            did: this.selectTransmitterDid, // 発信機DID
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 選択した発信機名を画面メッセージとして設定
            const selectTransmitterObj = this.transmitterList.find(item => item.value === this.selectTransmitterDid);
            this.displayInfoSelectTransmitter = selectTransmitterObj.text;

            // 発信機設定値一覧を設定
            this.valueList = this.getValueList();

            // 初期表示として選択発信機設定値を選択状態に設定
            this.selectValue = response.data.user_data.data_record;
            // 現在の選択発信機設定値を画面メッセージとして設定
            const selectValueObj = this.valueList.find(item => item.value === this.selectValue);
            // 発信機設定値一覧に、現在値と等しい項目が存在しない場合、レスポンスが｢自由表示｣列以外の値の為、現在値の表示をレスポンスのまま(16進数文字列(0x付き))とする
            this.displayInfoCurrentValue = selectValueObj ? selectValueObj.text : `0x${response.data.user_data.data_record.toUpperCase()}`;
            // 発信機設定値一覧に、現在値と等しい項目が存在しない場合、レスポンスが｢自由表示｣列以外の値の為、selectValueを空にしておく
            if (!selectValueObj) {
              this.selectValue = '';
            }

            // 出力変更箇所表示画面に遷移
            this.showExecuteOutputChangePoint();
          } else {
            // [成功以外]
            // 失敗画面に遷移
            this.showFailedExecute();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
        this.isDisabledSelect = false;
      }
    },

    /**
     * 発信機設定値一覧取得処理
     * @returns 発信機設定値リスト
     */
    getValueList() {
      // 設定値は変域['00'~'FF']の内、DID表の定義値のみリストに設定
      const decValueList = [...Array(parseInt('FF', 16) + 1).keys()];
      const retValueList = [];
      let staValue = '';
      for (const decValue of decValueList) {
        const value = util.addPadding(decValue.toString(16), 2).toUpperCase();
        if (this.$te(`${this.i18nSpwsDidKey}.${this.selectTransmitterDid}_1_all_${value}`)) {
          // デフォルト値以外を設定
          retValueList.push({
            text: this.$t(`${this.i18nSpwsDidKey}.${this.selectTransmitterDid}_1_all_${value}`),
            value: value
          });
        } else if (staValue) {
          // 始点と終点の値で該当するデフォルト値を照合
          if (this.$te(`${this.i18nSpwsDidKey}.${this.selectTransmitterDid}_1_all_${staValue}_${value}`)) {
            // 始点から終点までの値を各デフォルト値に対応する設定値とする
            for (let defValue = parseInt(staValue, 16); defValue <= parseInt(value, 16); defValue++) {
              // 各デフォルト値を設定
              retValueList.push({
                text: this.$t(`${this.i18nSpwsDidKey}.${this.selectTransmitterDid}_1_all_${staValue}_${value}`),
                value: util.addPadding(defValue.toString(16), 2).toUpperCase()
              });
            }
            // 始点の値を初期化
            staValue = '';
          }
        } else {
          // 始点の値を保持
          staValue = value;
        }
      }
      return retValueList;
    },

    /**
     * データ書き込みメソッド
     */
    async writePostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'write',
            did: this.selectTransmitterDid, // 発信機DID
            data_record: this.selectValue,  // 発信機設定値
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 成功画面に遷移
            this.showSuccess();
          } else {
            // [成功以外]
            // 失敗画面に遷移
            this.showFailedExecute();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で通信バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 通信エラー画面に遷移
      this.showFailed();
    },

  }
};